/**
 * File System Access API Helper
 * Enables saving files to organized folder structure
 */

/**
 * Check if File System Access API is supported
 */
export function isFileSystemAccessSupported() {
    return 'showDirectoryPicker' in window;
}

/**
 * Let user pick a base folder for saving files
 * @returns {Promise<FileSystemDirectoryHandle|null>}
 */
export async function pickBaseFolder() {
    if (!isFileSystemAccessSupported()) {
        alert('Your browser does not support folder selection. Please use Chrome or Edge.');
        return null;
    }

    try {
        const handle = await window.showDirectoryPicker({
            mode: 'readwrite',
            startIn: 'documents',
        });
        return handle;
    } catch (e) {
        if (e.name !== 'AbortError') {
            console.error('Error picking folder:', e);
        }
        return null;
    }
}

/**
 * Get or create a subfolder
 * @param {FileSystemDirectoryHandle} parentHandle 
 * @param {string} folderName 
 * @returns {Promise<FileSystemDirectoryHandle>}
 */
async function getOrCreateFolder(parentHandle, folderName) {
    return await parentHandle.getDirectoryHandle(folderName, { create: true });
}

/**
 * Extract DSO code (3 digits) from filename
 * @param {string} fileName 
 * @returns {string|null}
 */
export function extractDsoCode(fileName) {
    const match = fileName.match(/(\d{3})-G/);
    return match ? match[1] : null;
}

/**
 * Get current month as 2-digit string
 * @returns {string}
 */
export function getCurrentMonth() {
    return String(new Date().getMonth() + 1).padStart(2, '0');
}

/**
 * Save a file to organized folder structure
 * Structure: baseFolder/{dsoCode}/{month}/filename.pdf
 * 
 * @param {FileSystemDirectoryHandle} baseFolderHandle - The base folder handle
 * @param {Blob} blob - The file content
 * @param {string} fileName - The file name
 * @param {string|null} dsoCode - Optional DSO code (will extract from filename if not provided)
 * @param {string|null} month - Optional month (will use current month if not provided)
 * @returns {Promise<{success: boolean, path: string, error?: string}>}
 */
export async function saveToOrganizedFolder(baseFolderHandle, blob, fileName, dsoCode = null, month = null) {
    try {
        // Extract DSO code from filename if not provided
        const code = dsoCode || extractDsoCode(fileName) || 'OTHER';
        const monthFolder = month || getCurrentMonth();

        // Create folder structure: base/code/month/
        const codeFolder = await getOrCreateFolder(baseFolderHandle, code);
        const monthFolderHandle = await getOrCreateFolder(codeFolder, monthFolder);

        // Create and write file
        const fileHandle = await monthFolderHandle.getFileHandle(fileName, { create: true });
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();

        return {
            success: true,
            path: `${code}/${monthFolder}/${fileName}`,
        };
    } catch (e) {
        console.error('Error saving file:', e);
        return {
            success: false,
            path: '',
            error: e.message,
        };
    }
}

/**
 * Save multiple files to organized folder structure
 * @param {FileSystemDirectoryHandle} baseFolderHandle 
 * @param {Array<{blob: Blob, fileName: string, refCode?: string}>} files 
 * @returns {Promise<Array<{fileName: string, success: boolean, path: string, error?: string}>>}
 */
export async function saveAllToOrganizedFolder(baseFolderHandle, files) {
    const results = [];

    for (const file of files) {
        const dsoCode = file.refCode ? extractDsoCode(file.refCode) : extractDsoCode(file.fileName);
        const result = await saveToOrganizedFolder(baseFolderHandle, file.blob, file.fileName, dsoCode);
        results.push({
            fileName: file.fileName,
            ...result,
        });
    }

    return results;
}

/**
 * Fallback download for browsers that don't support File System Access API
 * @param {Blob} blob 
 * @param {string} fileName 
 */
export function fallbackDownload(blob, fileName) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
}
